import { useState } from "react";
import { Link } from "react-router-dom";
import { Film, Loader2, RefreshCw, Wand2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

/** Videos that no longer play, and the ones we can put back without an editor
 *  touching them — a malformed embed address, or the last good URL still
 *  sitting in the article's own revision history. */
export default function KbEmbedRepairCard({ onRepaired }) {
  const [scan, setScan] = useState(null);
  const [busy, setBusy] = useState("");

  const run = async () => {
    setBusy("scan");
    try {
      const { data } = await api.get("/kb/embeds/scan");
      setScan(data);
      if (!data.problems) toast.success("Every video in the KB still plays");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't scan the videos");
    } finally { setBusy(""); }
  };

  const repair = async () => {
    const fixes = (scan?.articles || []).flatMap((g) =>
      g.items.filter((i) => i.fix).map((i) => ({ slug: g.slug, old: i.src, new: i.fix })));
    if (!fixes.length) return;
    setBusy("repair");
    try {
      const { data } = await api.post("/kb/embeds/repair", { fixes });
      toast.success(`${data.repaired} video${data.repaired === 1 ? "" : "s"} put back`);
      (data.skipped || []).forEach((s) => toast.warning(`${s.slug}: ${s.why}`));
      await run();
      onRepaired?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't repair those");
    } finally { setBusy(""); }
  };

  const fixable = (scan?.articles || []).reduce(
    (n, g) => n + g.items.filter((i) => i.fix).length, 0);

  return (
    <Card className="px-5 py-4" data-testid="kb-embed-repair-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="flex items-center gap-2 font-heading text-base font-semibold text-slate-800 dark:text-slate-100">
            <Film className="h-4 w-4 text-slate-400" /> Video embeds
          </h3>
          <p className="mt-1 max-w-2xl text-xs text-slate-500 dark:text-slate-400">
            {scan
              ? `${scan.checked} video${scan.checked === 1 ? "" : "s"} checked — ${scan.problems} not playing, ${fixable} we can put back automatically.`
              : "Check every video in the KB and mend the ones whose address was mangled by an old save."}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Button size="sm" variant="outline" onClick={run} disabled={!!busy}
            data-testid="kb-embed-scan-button">
            {busy === "scan" ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
              : <RefreshCw className="mr-1.5 h-3.5 w-3.5" />}
            Scan videos
          </Button>
          {!!fixable && (
            <Button size="sm" onClick={repair} disabled={!!busy}
              data-testid="kb-embed-repair-button">
              {busy === "repair" ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                : <Wand2 className="mr-1.5 h-3.5 w-3.5" />}
              Put {fixable} back
            </Button>
          )}
        </div>
      </div>

      {!!scan?.articles?.length && (
        <ul className="mt-3 space-y-2" data-testid="kb-embed-scan-results">
          {scan.articles.map((g) => (
            <li key={g.slug} className="rounded-lg border border-slate-100 dark:border-slate-800 bg-slate-50/70 dark:bg-slate-900/40 px-3 py-2">
              <Link to={`/kb/a/${g.slug}`} data-testid={`kb-embed-open-${g.slug}`}
                className="text-xs font-semibold text-slate-700 dark:text-slate-200 hover:text-primary">
                {g.article}
              </Link>
              {g.items.map((it, i) => (
                <div key={`${it.src}-${i}`} className="mt-1.5">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ${
                      it.status === "broken" ? "bg-rose-100 text-rose-700" : "bg-amber-100 text-amber-700"}`}>
                      {it.status === "broken" ? "Not playing" : "Can't verify"}
                    </span>
                    <span className="text-xs font-medium text-slate-700 dark:text-slate-200">{it.host}</span>
                    <span className="text-xs text-slate-500 dark:text-slate-400">{it.reason}</span>
                  </div>
                  <p className="mt-0.5 break-all font-mono text-[11px] text-slate-400 dark:text-slate-500">{it.src}</p>
                  {it.fix ? (
                    <p className="mt-0.5 break-all text-[11px] text-emerald-700 dark:text-emerald-400"
                      data-testid={`kb-embed-fix-${g.slug}-${i}`}>
                      Will become {it.fix} — {it.how}
                    </p>
                  ) : (
                    <p className="mt-0.5 text-[11px] text-slate-500 dark:text-slate-400">
                      Nothing to mend from the address — open History on the article and restore
                      the version from before it broke.
                    </p>
                  )}
                </div>
              ))}
            </li>
          ))}
        </ul>
      )}
      {scan && !scan.articles.length && (
        <p className="mt-3 text-xs text-slate-500 dark:text-slate-400" data-testid="kb-embed-scan-clean">
          Nothing to fix — every video in the KB answered.
        </p>
      )}
    </Card>
  );
}
